import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  IoSave, IoFolderOpen, IoAdd, IoPrint, 
  IoSearch, IoCalendarOutline, IoShareSocial,
  IoHappyOutline, IoListOutline, IoChevronDown,
  IoLogoFacebook, IoMail, IoLogoWhatsapp, 
  IoLogoReddit, IoLink, IoDocument, IoCheckmarkDone, IoDesktop
} from 'react-icons/io5';
import ConfirmModal from './ConfirmModal';

const AUTO_SAVE_INTERVAL = 1000; // 1 second
const LOCAL_STORAGE_KEY = 'onlineNotepadContent';

const Notepad = () => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving'
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showFontMenu, setShowFontMenu] = useState(false);
  
  const textareaRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const insertMenuRef = useRef(null);
  const shareMenuRef = useRef(null);
  const fontMenuRef = useRef(null);
  
  const [showFontFamilies, setShowFontFamilies] = useState(false);
  const [showFontSizes, setShowFontSizes] = useState(false);
  
  // 确认对话框状态
  const [showConfirmNew, setShowConfirmNew] = useState(false);
  
  // 初始化内容
  useEffect(() => {
    const savedContent = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedContent) {
      setContent(savedContent);
      updateCounts(savedContent);
    }
  }, []);
  
  // 自动保存
  useEffect(() => {
    const autoSave = () => {
      if (content) {
        setSaveStatus('saving');
        localStorage.setItem(LOCAL_STORAGE_KEY, content);
        setTimeout(() => setSaveStatus('saved'), 300);
      }
    };
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(autoSave, AUTO_SAVE_INTERVAL);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [content]);
  
  // 点击外部关闭插入菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (insertMenuRef.current && !insertMenuRef.current.contains(event.target)) {
        setShowInsertMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // 点击外部关闭分享菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // 点击外部关闭字体菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fontMenuRef.current && !fontMenuRef.current.contains(event.target)) {
        setShowFontMenu(false);
        setShowFontFamilies(false);
        setShowFontSizes(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // 计算字数和字符数
  const updateCounts = (text) => {
    setCharCount(text.length);
    const words = text.trim() ? text.trim().split(/\s+/) : [];
    setWordCount(words.length);
  };
  
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    updateCounts(newContent);
    setSaveStatus('saving');
  };
  
  const handleSave = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'note.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const handleOpen = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target.result;
          setContent(content);
          updateCounts(content);
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };
  
  const handleNew = () => {
    setShowConfirmNew(true);
  };
  
  const confirmNew = () => {
    setContent('');
    updateCounts('');
    setShowConfirmNew(false);
  };
  
  const cancelNew = () => {
    setShowConfirmNew(false);
  };
  
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Note</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.6;
              padding: 20px;
              white-space: pre-wrap;
            }
          </style>
        </head>
        <body>
          ${content.replace(/\n/g, '<br>')}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  
  const toggleFindReplace = () => {
    setShowFindReplace(!showFindReplace);
    if (!showFindReplace) {
      setTimeout(() => {
        document.getElementById('findInput')?.focus();
      }, 100);
    }
  };
  
  const toggleInsertMenu = () => {
    setShowInsertMenu(!showInsertMenu);
  };
  
  const handleFind = () => {
    if (!findText) return;
    
    const textarea = textareaRef.current;
    const textContent = textarea.value;
    const startPos = textarea.selectionStart;
    
    const findPos = textContent.indexOf(findText, startPos);
    
    if (findPos !== -1) {
      textarea.focus();
      textarea.setSelectionRange(findPos, findPos + findText.length);
    } else {
      // 如果没找到，从头开始找
      const firstPos = textContent.indexOf(findText);
      if (firstPos !== -1) {
        textarea.focus();
        textarea.setSelectionRange(firstPos, firstPos + findText.length);
      } else {
        alert(t('editor.textNotFound'));
      }
    }
  };
  
  const handleReplace = () => {
    if (!findText) return;
    
    const textarea = textareaRef.current;
    const textContent = textarea.value;
    const selStart = textarea.selectionStart;
    const selEnd = textarea.selectionEnd;
    
    if (selEnd - selStart === findText.length && 
        textContent.substring(selStart, selEnd) === findText) {
      const newContent = 
        textContent.substring(0, selStart) + 
        replaceText + 
        textContent.substring(selEnd);
      
      setContent(newContent);
      updateCounts(newContent);
      
      textarea.focus();
      textarea.setSelectionRange(selStart + replaceText.length, selStart + replaceText.length);
    } else {
      handleFind();
    }
  };
  
  const handleReplaceAll = () => {
    if (!findText) return;
    
    const newContent = content.split(findText).join(replaceText);
    setContent(newContent);
    updateCounts(newContent);
  };
  
  const insertAtCursor = (textToInsert) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const selStart = textarea.selectionStart;
    const selEnd = textarea.selectionEnd;
    
    const newContent = 
      content.substring(0, selStart) + 
      textToInsert + 
      content.substring(selEnd);
    
    setContent(newContent);
    updateCounts(newContent);
    
    // 设置光标位置到插入内容后
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        selStart + textToInsert.length, 
        selStart + textToInsert.length
      );
    }, 10);
    
    setShowInsertMenu(false);
  };
  
  const insertDate = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    insertAtCursor(dateStr);
  };
  
  const insertDateTime = () => {
    const now = new Date();
    const dateTimeStr = now.toLocaleString();
    insertAtCursor(dateTimeStr);
  };
  
  const insertEmoji = (emoji) => {
    insertAtCursor(emoji);
  };
  
  const insertNumberedList = () => {
    insertAtCursor("\n1. \n2. \n3. \n");
  };
  
  const toggleShareMenu = () => {
    setShowShareMenu(!showShareMenu);
  };
  
  const shareToFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(content.substring(0, 300) + (content.length > 300 ? '...' : ''));
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, '_blank');
    setShowShareMenu(false);
  };
  
  const shareToWhatsApp = () => {
    const text = encodeURIComponent(content.substring(0, 300) + (content.length > 300 ? '...' : ''));
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowShareMenu(false);
  };
  
  const shareByEmail = () => {
    const subject = encodeURIComponent('My Online Notepad Content');
    const body = encodeURIComponent(content);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setShowShareMenu(false);
  };
  
  const shareToReddit = () => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent('Shared from Online Notepad');
    window.open(`https://www.reddit.com/submit?url=${url}&title=${title}`, '_blank');
    setShowShareMenu(false);
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(content)
      .then(() => {
        alert(t('editor.copiedToClipboard'));
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
    setShowShareMenu(false);
  };
  
  const toggleFontMenu = () => {
    if (showFontMenu) {
      setShowFontMenu(false);
      setShowFontFamilies(false);
      setShowFontSizes(false);
    } else {
      setShowFontMenu(true);
    }
  };
  
  const showFontFamilyOptions = () => {
    setShowFontFamilies(true);
    setShowFontSizes(false);
  };
  
  const showFontSizeOptions = () => {
    setShowFontSizes(true);
    setShowFontFamilies(false);
  };
  
  const changeFont = (fontFamily) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    textarea.style.fontFamily = fontFamily;
    setShowFontMenu(false);
    setShowFontFamilies(false);
  };
  
  const changeFontSize = (fontSize) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    textarea.style.fontSize = fontSize;
    setShowFontMenu(false);
    setShowFontSizes(false);
  };
  
  return (
    <div className="notepad">
      <div className="toolbar">
        <div className="toolbar-group">
          <button className="tool-button" onClick={handleNew} title={t('editor.new')}>
            <IoAdd />
            <span>{t('editor.new')}</span>
          </button>
          <button className="tool-button" onClick={handleOpen} title={t('editor.open')}>
            <IoFolderOpen />
            <span>{t('editor.open')}</span>
          </button>
          
          <div className="insert-menu-container" ref={insertMenuRef}>
            <button className="tool-button" onClick={toggleInsertMenu} title={t('editor.insert')}>
              <IoAdd />
              <span>{t('editor.insert')}</span>
              <IoChevronDown size={12} />
            </button>
            
            {showInsertMenu && (
              <div className="insert-dropdown slide-up">
                <div className="insert-option" onClick={insertDate}>
                  <IoCalendarOutline />
                  <span>{t('editor.insertDate')}</span>
                </div>
                <div className="insert-option" onClick={insertDateTime}>
                  <IoCalendarOutline />
                  <span>{t('editor.insertDateTime')}</span>
                </div>
                <div className="insert-option" onClick={() => insertEmoji('😊')}>
                  <IoHappyOutline />
                  <span>{t('editor.insertEmoji')}</span>
                </div>
                <div className="insert-option emoji-grid">
                  {['😊', '👍', '🎉', '❤️', '😂', '🔥', '✅', '⭐'].map((emoji) => (
                    <span 
                      key={emoji} 
                      className="emoji-item" 
                      onClick={() => insertEmoji(emoji)}
                    >
                      {emoji}
                    </span>
                  ))}
                </div>
                <div className="insert-option" onClick={insertNumberedList}>
                  <IoListOutline />
                  <span>{t('editor.insertNumberedList')}</span>
                </div>
              </div>
            )}
          </div>
          
          <button className="tool-button" onClick={handleSave} title={t('editor.save')}>
            <IoSave />
            <span>{t('editor.save')}</span>
          </button>
          <button className="tool-button" onClick={handlePrint} title={t('editor.print')}>
            <IoPrint />
            <span>{t('editor.print')}</span>
          </button>
          
          <div className="font-menu-container" ref={fontMenuRef}>
            <button className="tool-button" onClick={toggleFontMenu} title={t('editor.font')}>
              <IoDocument />
              <span>{t('editor.font')}</span>
              <IoChevronDown size={12} />
            </button>
            
            {showFontMenu && (
              <div className="font-dropdown slide-up">
                {!showFontFamilies && !showFontSizes ? (
                  <>
                    <div className="font-option" onClick={showFontFamilyOptions}>
                      <span>{t('editor.fontFamily')}</span>
                    </div>
                    <div className="font-option" onClick={showFontSizeOptions}>
                      <span>{t('editor.fontSize')}</span>
                    </div>
                  </>
                ) : showFontFamilies ? (
                  <div className="font-section">
                    <div className="font-header">
                      <button className="back-button" onClick={() => setShowFontFamilies(false)}>
                        ←
                      </button>
                      {t('editor.fontFamily')}
                    </div>
                    <div className="font-option" onClick={() => changeFont('黑体, SimHei, sans-serif')}>
                      <span style={{ fontFamily: '黑体, SimHei, sans-serif' }}>{t('editor.fonts.heiti')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFont('宋体, SimSun, serif')}>
                      <span style={{ fontFamily: '宋体, SimSun, serif' }}>{t('editor.fonts.songti')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFont('楷体, KaiTi, serif')}>
                      <span style={{ fontFamily: '楷体, KaiTi, serif' }}>{t('editor.fonts.kaiti')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFont('微软雅黑, Microsoft YaHei, sans-serif')}>
                      <span style={{ fontFamily: '微软雅黑, Microsoft YaHei, sans-serif' }}>{t('editor.fonts.yahei')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFont('-apple-system, BlinkMacSystemFont, sans-serif')}>
                      <span style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>{t('editor.fonts.system')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="font-section">
                    <div className="font-header">
                      <button className="back-button" onClick={() => setShowFontSizes(false)}>
                        ←
                      </button>
                      {t('editor.fontSize')}
                    </div>
                    <div className="font-option" onClick={() => changeFontSize('12px')}>
                      <span style={{ fontSize: '12px' }}>{t('editor.fontSizes.small')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFontSize('14px')}>
                      <span style={{ fontSize: '14px' }}>{t('editor.fontSizes.medium')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFontSize('16px')}>
                      <span style={{ fontSize: '16px' }}>{t('editor.fontSizes.normal')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFontSize('18px')}>
                      <span style={{ fontSize: '18px' }}>{t('editor.fontSizes.large')}</span>
                    </div>
                    <div className="font-option" onClick={() => changeFontSize('20px')}>
                      <span style={{ fontSize: '20px' }}>{t('editor.fontSizes.xlarge')}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <button className="tool-button" onClick={toggleFindReplace} title={t('editor.find')}>
            <IoSearch />
            <span>{t('editor.find')}</span>
          </button>
          
          <div className="share-menu-container" ref={shareMenuRef}>
            <button className="tool-button" onClick={toggleShareMenu} title={t('editor.share')}>
              <IoShareSocial />
              <span>{t('editor.share')}</span>
              <IoChevronDown size={12} />
            </button>
            
            {showShareMenu && (
              <div className="share-dropdown slide-up">
                <div className="share-header">{t('editor.shareVia')}</div>
                
                <div className="share-grid">
                  <div className="share-option" onClick={shareToFacebook}>
                    <div className="share-icon facebook">
                      <IoLogoFacebook size={20} />
                    </div>
                    <span>Facebook</span>
                  </div>
                  
                  <div className="share-option" onClick={shareToWhatsApp}>
                    <div className="share-icon whatsapp">
                      <IoLogoWhatsapp size={20} />
                    </div>
                    <span>WhatsApp</span>
                  </div>
                  
                  <div className="share-option" onClick={shareByEmail}>
                    <div className="share-icon email">
                      <IoMail size={20} />
                    </div>
                    <span>Email</span>
                  </div>
                  
                  <div className="share-option" onClick={shareToReddit}>
                    <div className="share-icon reddit">
                      <IoLogoReddit size={20} />
                    </div>
                    <span>Reddit</span>
                  </div>
                  
                  <div className="share-option" onClick={copyToClipboard}>
                    <div className="share-icon copy">
                      <IoLink size={20} />
                    </div>
                    <span>{t('editor.copyToClipboard')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="word-count">
          {wordCount} {t('editor.words')}, {charCount} {t('editor.characters')}
        </div>
      </div>
      
      {showFindReplace && (
        <div className="find-replace-bar">
          <div className="find-replace-group">
            <input
              id="findInput"
              type="text"
              className="find-replace-input"
              placeholder={t('editor.find')}
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
            />
            <button className="find-replace-button" onClick={handleFind}>
              {t('editor.find')}
            </button>
          </div>
          
          <div className="find-replace-group">
            <input
              type="text"
              className="find-replace-input"
              placeholder={t('editor.replace')}
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
            />
            <button className="find-replace-button" onClick={handleReplace}>
              {t('editor.replace')}
            </button>
            <button className="find-replace-button primary" onClick={handleReplaceAll}>
              {t('editor.replaceAll')}
            </button>
          </div>
        </div>
      )}
      
      <textarea
        ref={textareaRef}
        className="notepad-content"
        value={content}
        onChange={handleContentChange}
        placeholder={t('editor.placeholder')}
        spellCheck={true}
      ></textarea>
      
      <div className="editor-status">
        <span className={`save-status ${saveStatus}`}>
          {saveStatus === 'saved' ? t('editor.saveStatus.saved') : t('editor.saveStatus.saving')}
        </span>
      </div>
      
      <div className="notepad-info">
        <div className="info-section">
          <h2 className="info-heading">{t('info.overview.title')}</h2>
          <p className="info-text">
            {t('info.overview.content').split('.').map((sentence, index, array) => {
              if (index === 1 || index === 2 || index === 4) {
                return <strong key={index}>{sentence}.</strong>;
              } else if (index === 6 || index === 7) {
                return <em key={index} className="highlight">{sentence}.</em>;
              } else if (index === array.length - 1) {
                return <span key={index}>{sentence}</span>;
              } else {
                return <span key={index}>{sentence}.</span>;
              }
            })}
          </p>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.howItWorks.title')}</h2>
          <p className="info-text">
            {t('info.howItWorks.content').split('.').map((sentence, index, array) => {
              if (index === 0 || index === 1) {
                return <span key={index}>{sentence}.</span>;
              } else if (index === 2) {
                return <em key={index} className="highlight">{sentence}.</em>;
              } else if (index === 3) {
                return <strong key={index}>{sentence}.</strong>;
              } else if (index === array.length - 1) {
                return <span key={index}>{sentence}</span>;
              } else {
                return <span key={index}>{sentence}.</span>;
              }
            })}
          </p>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.browserCompatibility.title')}</h2>
          <p className="info-text">
            {t('info.browserCompatibility.content').split('.').map((sentence, index, array) => {
              if (index === 0) {
                return <span key={index}>{sentence}. <strong>{sentence.includes('Google') ? sentence : ''}</strong></span>;
              } else if (index === array.length - 1) {
                return <span key={index}>{sentence}</span>;
              } else {
                return <span key={index}>{sentence}.</span>;
              }
            })}
          </p>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.about.title')}</h2>
          <p className="info-text">
            {t('info.about.content').split('.').map((sentence, index, array) => {
              if (index === 0) {
                return <span key={index}>{sentence}. <strong>{sentence.includes('free') ? sentence : ''}</strong></span>;
              } else if (index === array.length - 1) {
                return <span key={index}>{sentence}</span>;
              } else {
                return <span key={index}>{sentence}.</span>;
              }
            })}
          </p>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.features.title')}</h2>
          <p className="info-text">
            {t('info.features.content')}
          </p>
          <ul className="info-list">
            {t('info.features.list', { returnObjects: true }).map((item, index) => {
              const parts = item.split(':');
              if (parts.length > 1) {
                return (
                  <li key={index}>
                    <strong>{parts[0]}:</strong>{parts[1]}
                  </li>
                );
              }
              return <li key={index}>{item}</li>;
            })}
          </ul>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.tips.title')}</h2>
          <p className="info-text">
            {t('info.tips.content')}
          </p>
          <ul className="info-list">
            {t('info.tips.list', { returnObjects: true }).map((item, index) => {
              if (item.includes("'Save As'") || item.includes("find and replace") || item.includes("word counter")) {
                const parts = item.split(/('.*?'|find and replace|word counter)/g);
                return (
                  <li key={index}>
                    {parts.map((part, i) => {
                      if (part === "'Save As'" || part === "find and replace" || part === "word counter") {
                        return <strong key={i}>{part}</strong>;
                      }
                      return part;
                    })}
                  </li>
                );
              } else if (item.includes("persist between sessions")) {
                return (
                  <li key={index}>
                    {item.split("persist between sessions").map((part, i, arr) => {
                      if (i === 0) {
                        return <span key={i}>{part}<em className="highlight">persist between sessions</em></span>;
                      }
                      return <span key={i}>{part}</span>;
                    })}
                  </li>
                );
              }
              return <li key={index}>{item}</li>;
            })}
          </ul>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.privacy.title')}</h2>
          <p className="info-text">
            {t('info.privacy.content').split('.').map((sentence, index, array) => {
              if (sentence.includes("locally in your browser")) {
                return (
                  <span key={index}>
                    {sentence.split("locally in your browser").map((part, i, arr) => {
                      if (i === 0) {
                        return <span key={i}>{part}<strong>locally in your browser</strong></span>;
                      }
                      return <span key={i}>{part}</span>;
                    })}.
                  </span>
                );
              } else if (sentence.includes("private and secure")) {
                return (
                  <span key={index}>
                    {sentence.split("private and secure").map((part, i, arr) => {
                      if (i === 0) {
                        return <span key={i}>{part}<em className="highlight">private and secure</em></span>;
                      }
                      return <span key={i}>{part}</span>;
                    })}.
                  </span>
                );
              } else if (index === array.length - 1) {
                return <span key={index}>{sentence}</span>;
              } else {
                return <span key={index}>{sentence}.</span>;
              }
            })}
          </p>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.quickAccess.title')}</h2>
          <p className="info-text">
            {t('info.quickAccess.content').split('.').map((sentence, index, array) => {
              if (sentence.includes("bookmarking this page")) {
                return (
                  <span key={index}>
                    {sentence.split("bookmarking this page").map((part, i, arr) => {
                      if (i === 0) {
                        return <span key={i}>{part}<strong>bookmarking this page</strong></span>;
                      }
                      return <span key={i}>{part}</span>;
                    })}.
                  </span>
                );
              } else if (sentence.includes("Ctrl+D") || sentence.includes("Command+D")) {
                return (
                  <span key={index}>
                    {sentence.replace(/(Ctrl\+D|Command\+D)/g, match => `<strong>${match}</strong>`).split('<strong>').map((part, i, arr) => {
                      if (i === 0) {
                        return <span key={i}>{part}</span>;
                      }
                      const [bold, rest] = part.split('</strong>');
                      return (
                        <span key={i}>
                          <strong>{bold}</strong>
                          {rest}
                        </span>
                      );
                    })}.
                  </span>
                );
              } else if (index === array.length - 1) {
                return <span key={index}>{sentence}</span>;
              } else {
                return <span key={index}>{sentence}.</span>;
              }
            })}
          </p>
        </div>
        
        <div className="info-section">
          <h2 className="info-heading">{t('info.helpPage.title')}</h2>
          <p className="info-text">
            {t('info.helpPage.content').split('.').map((sentence, index, array) => {
              if (sentence.includes("help button")) {
                return (
                  <span key={index}>
                    {sentence.split("help button").map((part, i, arr) => {
                      if (i === 0) {
                        return <span key={i}>{part}<strong>help button</strong></span>;
                      }
                      return <span key={i}>{part}</span>;
                    })}.
                  </span>
                );
              } else if (sentence.includes("features overview")) {
                return (
                  <span key={index}>
                    {sentence.split("features overview").map((part, i, arr) => {
                      if (i === 0) {
                        return <span key={i}>{part}<em className="highlight">features overview</em></span>;
                      }
                      return <span key={i}>{part}</span>;
                    })}.
                  </span>
                );
              } else if (index === array.length - 1) {
                return <span key={index}>{sentence}</span>;
              } else {
                return <span key={index}>{sentence}.</span>;
              }
            })}
          </p>
          <ul className="info-list">
            {t('info.helpPage.list', { returnObjects: true }).map((item, index) => {
              if (item.includes("autosave") || item.includes("word count") || item.includes("find and replace")) {
                const parts = item.split(/(autosave|word count|find and replace)/g);
                return (
                  <li key={index}>
                    {parts.map((part, i) => {
                      if (part === "autosave" || part === "word count" || part === "find and replace") {
                        return <strong key={i}>{part}</strong>;
                      }
                      return part;
                    })}
                  </li>
                );
              }
              return <li key={index}>{item}</li>;
            })}
          </ul>
        </div>
        
        <div className="info-section features-section">
          <h2 className="info-heading">{t('info.featureOverview.title')}</h2>
          <div className="overview-container">
            <p className="info-text">{t('app.overview')}</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon"><IoSave size={24} /></div>
              <h3 className="feature-title">{t('features.autosave')}</h3>
              <p className="feature-description">{t('features.autosaveDesc')}</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon"><IoDocument size={24} /></div>
              <h3 className="feature-title">{t('features.wordCount')}</h3>
              <p className="feature-description">{t('features.wordCountDesc')}</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon"><IoSearch size={24} /></div>
              <h3 className="feature-title">{t('features.findReplace')}</h3>
              <p className="feature-description">{t('features.findReplaceDesc')}</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon"><IoCheckmarkDone size={24} /></div>
              <h3 className="feature-title">{t('features.spellcheck')}</h3>
              <p className="feature-description">{t('features.spellcheckDesc')}</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon"><IoPrint size={24} /></div>
              <h3 className="feature-title">{t('features.print')}</h3>
              <p className="feature-description">{t('features.printDesc')}</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon"><IoDesktop size={24} /></div>
              <h3 className="feature-title">{t('app.subtitle')}</h3>
              <p className="feature-description">{t('app.description')}</p>
            </div>
          </div>
        </div>
        
      </div>
      
      {/* 确认新建对话框 */}
      <ConfirmModal
        isOpen={showConfirmNew}
        message={t('editor.confirmNew')}
        onConfirm={confirmNew}
        onCancel={cancelNew}
      />
    </div>
  );
};

export default Notepad; 